import ProposalLink from "@client/components/utils/ProposalLink";
import { Tooltip } from "@client/components/utils/Tooltip";
import { StateContext } from "@client/pages/round/Round";
import { memberIsMeanRecommender } from "@client/utils/memberIsMeanRecommender";
import { RecentIndicator } from "@client/utils/recentProposals";
import { useTheme } from "@client/utils/useTheme";
import { TinyColor } from "@ctrl/tinycolor";
import {
  getEffectiveTrack,
  getRoundMemberForEvaluator,
} from "@engine/round/accessors";
import { Recommender } from "@engine/round/aggregate";
import clsx from "clsx";
import { format } from "d3-format";
import { sortBy } from "lodash";
import { useContext, useState } from "react";
import {
  calculateComparisonsMatrixData,
  ComparisonsMatrixData,
  ComparisonsMatrixRow,
} from "./calculateComparisonsMatrixData";
import { calculateMeanAndStdDev, MatrixTable, MatrixTableEl } from "./common";

const sectionNameToType = {
  "First Dollar Value": "fdv",
  "Last Dollar Threshold": "ldt",
  Fullness: "fullness",
} as const;

type SectionName = keyof typeof sectionNameToType;

type SortOrder = {
  sectionName: SectionName;
  recommenderId: string;
};

export function ComparisonsMatrix({
  showMembersOfOtherTracks,
  showRowsWithNoData,
}: {
  showMembersOfOtherTracks: boolean;
  showRowsWithNoData: boolean;
}) {
  const { round, actingAsUserId, mode } = useContext(StateContext);
  const viewerTrack = getEffectiveTrack(mode, round.members[actingAsUserId]);
  const comparisonsMatrixData = calculateComparisonsMatrixData(round);
  const [sortOrder, setSortOrder] = useState<SortOrder>({
    sectionName: "First Dollar Value",
    recommenderId: "overall",
  });

  const values = getAllComparisonsValues(comparisonsMatrixData);
  const statistics = {
    fdv: calculateMeanAndStdDev(values.fdv),
    ldt: calculateMeanAndStdDev(values.ldt),
    fullness: calculateMeanAndStdDev(values.fullness),
  };

  function getRowSortOrderValue(
    row: ComparisonsMatrixRow,
    sortOrder: SortOrder,
  ): number {
    const sectionType = sectionNameToType[sortOrder.sectionName];
    if (sortOrder.recommenderId === "overall") {
      return Object.values(row.sections[sectionType].recommenderValues).reduce(
        (total: number, value) =>
          total + (typeof value === "number" ? Math.abs(value) : 0),
        0,
      );
    } else {
      const value =
        row.sections[sectionType].recommenderValues[sortOrder.recommenderId];
      return typeof value === "number" ? Math.abs(value) : 0;
    }
  }

  const sortedRows = sortBy(comparisonsMatrixData.rows, (row) =>
    getRowSortOrderValue(row, sortOrder),
  ).reverse();

  const filteredRecommenders =
    showMembersOfOtherTracks || viewerTrack === "NO_TRACK"
      ? comparisonsMatrixData.recommenders
      : comparisonsMatrixData.recommenders.filter((recommender) => {
          const member = getRoundMemberForEvaluator(round, recommender.id);
          return (
            member &&
            (memberIsMeanRecommender(member) || member.track === viewerTrack)
          );
        });

  return (
    <MatrixTableEl>
      <MatrixTable>
        <thead className="sticky top-0 z-10">
          <tr>
            <th rowSpan={2} />
            <ComparisonsColumnSectionHeadersRow
              setSortOrder={setSortOrder}
              sortOrder={sortOrder}
              filteredRecommenders={filteredRecommenders}
            />
          </tr>
          <tr>
            <ComparisonsColumnHeadersRow
              setSortOrder={setSortOrder}
              sortOrder={sortOrder}
              filteredRecommenders={filteredRecommenders}
            />
            <td />
          </tr>
        </thead>
        <tbody>
          {sortedRows
            .filter((row) => {
              if (showRowsWithNoData) {
                return true;
              }
              for (const section of Object.values(row.sections)) {
                for (const [key, value] of Object.entries(
                  section.recommenderValues,
                )) {
                  if (
                    filteredRecommenders.find((r) => r.id === key) &&
                    value != null
                  ) {
                    return true;
                  }
                }
              }
              return false;
            })
            .map((row, i) => (
              <ComparisonsProposalRow
                key={row.proposal.id}
                row={row}
                recommenders={filteredRecommenders}
                statistics={statistics}
                firstRow={i === 0}
                isHold={row.proposal.id === round.holdProposalId}
              />
            ))}
        </tbody>
      </MatrixTable>
    </MatrixTableEl>
  );
}

function ComparisonsColumnSectionHeadersRow({
  setSortOrder,
  sortOrder,
  filteredRecommenders,
}: {
  setSortOrder: (sortOrder: SortOrder) => void;
  sortOrder: SortOrder;
  filteredRecommenders: Recommender[];
}) {
  const sectionColSpan = filteredRecommenders.length;
  return (
    <>
      {Object.keys(sectionNameToType).map((sectionName, i) => {
        const sortedOnMe =
          sortOrder.sectionName === sectionName &&
          sortOrder.recommenderId === "overall";

        return (
          <th
            className={clsx(
              "p-1 min-w-[48px] mr-2",
              "border-y border-r border-solid light:border-black dark:border-[#181818]",
              i === 0 && "border-l",
              "light:bg-white dark:bg-white/15",
            )}
            colSpan={sectionColSpan}
            key={sectionName}
            onClick={() =>
              setSortOrder({
                sectionName: sectionName as SectionName,
                recommenderId: "overall",
              })
            }
          >
            <strong>
              <Tooltip title={sectionName} placement="bottom-start">
                {sectionName + (sortedOnMe ? " ▼" : " ▶")}
              </Tooltip>
            </strong>
          </th>
        );
      })}
    </>
  );
}

function ComparisonsColumnHeadersRow({
  setSortOrder,
  sortOrder,
  filteredRecommenders,
}: {
  setSortOrder: (sortOrder: SortOrder) => void;
  sortOrder: SortOrder;
  filteredRecommenders: Recommender[];
}) {
  return (
    <>
      {Object.keys(sectionNameToType).map((sectionName, i) => {
        return filteredRecommenders.map((recommender, j) => {
          const sortedOnMe =
            sortOrder.sectionName === sectionName &&
            sortOrder.recommenderId === recommender.id;

          return (
            <th
              className={clsx(
                "p-1 min-w-[48px] mr-2",
                "border-r border-b border-solid light:border-black dark:border-[#181818]",
                i === 0 && j === 0 && "border-l",
                "light:bg-white dark:bg-white/15",
              )}
              key={`${sectionName}-${recommender.id}`}
              onClick={() => {
                setSortOrder({
                  sectionName: sectionName as SectionName,
                  recommenderId: recommender.id,
                });
              }}
            >
              {recommender.shortname + (sortedOnMe ? " ▼" : " ▶")}
            </th>
          );
        });
      })}
    </>
  );
}

function getAllComparisonsValues(data: ComparisonsMatrixData): {
  fdv: number[];
  ldt: number[];
  fullness: number[];
} {
  const values = {
    fdv: [] as number[],
    ldt: [] as number[],
    fullness: [] as number[],
  };

  for (const row of data.rows) {
    for (const recommender of data.recommenders) {
      const fdvValue = row.sections.fdv.recommenderValues[recommender.id];
      if (typeof fdvValue === "number") {
        values.fdv.push(fdvValue);
      }

      const ldtValue = row.sections.ldt.recommenderValues[recommender.id];
      if (typeof ldtValue === "number") {
        values.ldt.push(ldtValue);
      }

      const fullnessValue =
        row.sections.fullness.recommenderValues[recommender.id];
      if (typeof fullnessValue === "number") {
        values.fullness.push(fullnessValue);
      }
    }
  }

  return values;
}

function ComparisonsProposalRow({
  row,
  recommenders,
  statistics,
  firstRow,
  isHold,
}: {
  row: ComparisonsMatrixRow;
  recommenders: Recommender[];
  statistics: {
    fdv: { mean: number; standardDeviation: number };
    ldt: { mean: number; standardDeviation: number };
    fullness: { mean: number; standardDeviation: number };
  };
  firstRow: boolean;
  isHold: boolean;
}) {
  return (
    <tr
      key={row.proposal.id}
      className="hover:filter light:hover:brightness-90 dark:hover:brightness-75"
    >
      <th
        className={clsx(
          "sticky left-0 z-10 p-1 min-w-[48px] mr-2 text-left",
          "light:bg-white dark:bg-[#3a3a3a]",
          "border-x border-b light:border-black dark:bg-white/15",
          firstRow && "border-t",
        )}
      >
        {isHold ? (
          <span>{row.proposal.shortname}</span>
        ) : (
          <ProposalLink proposal={row.proposal}>
            {row.proposal.shortname}
            <RecentIndicator proposalId={row.proposal.id} />
          </ProposalLink>
        )}
      </th>
      {Object.values(sectionNameToType).map((sectionType) =>
        recommenders.map((recommender) => (
          <Cell
            key={`${sectionType}-${recommender.id}`}
            value={row.sections[sectionType].recommenderValues[recommender.id]}
            standardDeviation={statistics[sectionType].standardDeviation}
            mean={statistics[sectionType].mean}
            formatValue={sectionType === "ldt"}
          />
        )),
      )}
    </tr>
  );
}

export function Cell({
  value,
  standardDeviation,
  mean,
  userInitials,
  formatValue = true,
}: {
  value: number | undefined | null;
  standardDeviation: number;
  mean: number;
  // A disagreements explanation tooltip will be shown if userInitials is provided
  userInitials?: {
    column: string;
    section: string;
  };
  formatValue?: boolean;
}) {
  const { isDarkMode } = useTheme();
  if (value == null) {
    return <MatrixCell />;
  }

  const zScore = Math.abs(value - mean) / standardDeviation;

  // consider 2 standard deviations to be the maximum of the color scale
  let alpha = Math.min(zScore, 2) / 2;

  const baseColor = value < 0 ? "red" : "green";
  let backgroundColor = new TinyColor(baseColor).setAlpha(alpha);

  if (baseColor === "red") {
    backgroundColor = isDarkMode
      ? backgroundColor.darken(25)
      : backgroundColor.lighten(25);
  }

  if (baseColor === "green") {
    backgroundColor = isDarkMode
      ? backgroundColor
      : backgroundColor.lighten(25);
  }

  const formattedValue = formatValue
    ? format(".2s")(value)
    : value && value.toFixed(2);

  let cellContent = <>{formattedValue}</>;
  if (userInitials != null) {
    const absValue = Math.abs(value);
    const formattedAbsValue = formatValue
      ? format(".2s")(absValue)
      : absValue.toFixed(2);
    const tooltipText = `${userInitials.column} would allocate ${formattedAbsValue} ${value < 0 ? "less" : "more"} compared to ${userInitials.section}`;
    cellContent = <Tooltip title={tooltipText}>{formattedValue}</Tooltip>;
  }
  return (
    <MatrixCell style={{ backgroundColor: backgroundColor.toRgbString() }}>
      {cellContent}
    </MatrixCell>
  );
}

export function MatrixCell({
  children,
  colSpan,
  onClick,
  style,
}: {
  children?: React.ReactNode;
  colSpan?: number;
  onClick?: () => void;
  style?: React.CSSProperties;
}) {
  return (
    <td
      className="p-1 min-w-[48px] mr-2 border-r border-b light:border-black light:bg-white dark:bg-white/15"
      colSpan={colSpan}
      onClick={onClick}
      style={style}
    >
      {children}
    </td>
  );
}
